// src/oura/screens/ClientSite.tsx
//
// The client's Website page (view "setup", the same view as the staff Site
// screen, at /oura/setup). Read-only: the same four answers staff open with
// (Site health, AI Readability, speed on a phone, the issues), then what the
// check found in plain words, what a machine can read, and whether the AI
// readers get in. Every card is the staff card or reads the same audit, so
// the two pages state one set of numbers.
//
// What it leaves to staff: Core Web Vitals' lab detail (the phone tile carries
// the verdict), Health by area, Strengths and weaknesses (its weaknesses are
// the findings again, in crawl words), the page list and Page workshop, every
// How to fix drawer and every refresh. The reviewer's out-of-scope pages are dropped
// here too, so an issue count reads the same on both sides.

import { useEffect, useMemo, useState, type ReactNode } from "react";
import type { Business, ScoreSnapshot } from "../contract";
import { getAudit, type BusinessAudit } from "../audit";
import { fetchStoredRolesResult } from "../pageRoles";
import type { PageRole } from "../pageWorkshop";
import { Bento, FoldScope, PageHead, REDUCED_MOTION, useMediaQuery } from "@oura/ui";
import { CLIENT_HINT } from "../nav";
import { MERCHANT_V2 } from "../merchantV2";
import { ClientFindingsCard } from "../components/site/ClientFindingsCard";
import { ClientReadersCard } from "../components/site/ClientReadersCard";
import { MachineReadCard } from "../components/site/MachineReadCard";
import { SiteTiles } from "../components/site/SiteTiles";
import { VisibilityTabs } from "../merchant/VisibilityTabs";
import { READABILITY_ABSENT_TEXT, SITE_HEALTH_ABSENT_TEXT, dayMonth } from "../components/site/siteCopy";
import { activeIssues, pagesWith, rolesByKey, scopeIssues, setAsideKeys } from "../components/site/siteIssues";

/** The pages staff set aside, as far as this page knows them. */
type RoleState = { kind: "loading" } | { kind: "ready"; roles: Map<string, PageRole> } | { kind: "error" };

/** The figures under the title: how much of the site this check read. */
function statusLine(audit: BusinessAudit, crawlDay: string | null, unread: string | null): string {
    if (unread) return unread;
    const n = audit.pagesCrawled;
    if (n <= 0) return crawlDay ? `Checked ${crawlDay}.` : "";
    const pages = `${n} ${n === 1 ? "page" : "pages"}`;
    return `We read ${pages} of your site${crawlDay ? ` on ${crawlDay}` : ""}.`;
}

export function ClientSite({
    snapshot,
    business,
    liveAudit,
    businessId,
    scanControl,
}: {
    snapshot: ScoreSnapshot;
    business: Business;
    /** Real data; the sample audit on a fixture business. */
    liveAudit?: BusinessAudit;
    /** The live business, for the reviewer's out-of-scope pages. Absent on a fixture. */
    businessId?: string;
    /** The past-scan picker, when the business has more than one scan. */
    scanControl?: ReactNode;
}) {
    const audit = liveAudit ?? getAudit(business, snapshot);
    // The redesign shows no issue counts, so it has no need of the pages
    // staff set aside, and does not read them.
    const rolesFor = MERCHANT_V2 ? undefined : businessId;
    const reducedMotion = useMediaQuery(REDUCED_MOTION);

    // The pages staff set aside on the Page workshop, from the shared record.
    // Without them a client would count issues on pages we chose not to work
    // on, and their number would disagree with ours. So no issue count shows
    // until the read lands, and a failed read says so rather than counting
    // every page (Jack's review of #4210). A sample business has none to read.
    const [roleState, setRoleState] = useState<RoleState>(() => (rolesFor ? { kind: "loading" } : { kind: "ready", roles: new Map() }));
    useEffect(() => {
        if (!rolesFor) {
            setRoleState({ kind: "ready", roles: new Map() });
            return;
        }
        let cancelled = false;
        setRoleState({ kind: "loading" });
        void fetchStoredRolesResult(rolesFor, business.domain).then(
            (server) => {
                if (!cancelled) setRoleState(server ? { kind: "ready", roles: rolesByKey(server) } : { kind: "error" });
            },
            () => {
                if (!cancelled) setRoleState({ kind: "error" });
            },
        );
        return () => {
            cancelled = true;
        };
    }, [rolesFor, business.domain]);
    const active = useMemo(
        () => (roleState.kind === "ready" ? activeIssues(scopeIssues(audit.issues, setAsideKeys(roleState.roles))) : null),
        [audit.issues, roleState],
    );
    const roleStatus = roleState.kind === "error"
        ? { text: "We could not load the pages included in this check. Reload to try again.", failed: true }
        : { text: "Loading the pages included in this check…", failed: false };

    const siteHealth = typeof audit.siteHealthScore === "number" ? audit.siteHealthScore : null;
    const crawlUnread = audit.crawlFailed === true || !!audit.siteHealthScoreAbsent;
    const crawlDay = dayMonth(audit.crawledAt) || null;
    const unreadText = crawlUnread
        ? (audit.siteHealthScoreAbsent && SITE_HEALTH_ABSENT_TEXT[audit.siteHealthScoreAbsent]) || "We could not read your site on this check."
        : null;

    const readabilityScore = typeof audit.aiReadabilityScore === "number" ? audit.aiReadabilityScore : null;
    const readabilityComponents = audit.aiReadabilityComponents ?? [];
    const readabilityAbsent = audit.aiReadabilityAbsent ?? null;
    // A reason only when the payload gives one: an absent score with no
    // reason is unknown, never "this check ran before the score existed"
    // (Jack's review of #4211).
    const readabilityAbsentText = (readabilityAbsent && READABILITY_ABSENT_TEXT[readabilityAbsent])
        || "Not measured on this check.";

    const openCard = (anchor: string) =>
        document.getElementById(anchor)?.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "start" });

    const machineRead = (
        <MachineReadCard
            client
            score={readabilityScore}
            verdict={audit.aiReadabilityVerdict ?? null}
            components={readabilityComponents}
            absentText={readabilityScore === null ? readabilityAbsentText : null}
            crawlLine={null}
            measuredAt={null}
            checkFor={() => null}
            busy={false}
        />
    );

    return (
        <FoldScope value="merchant-site">
            <PageHead
                over={business.name}
                // The redesign: Website is the second tab of Visibility.
                title={MERCHANT_V2 ? "Visibility" : "Website"}
                sentence={MERCHANT_V2 ? "How easily AI assistants and Google can read and load your site." : CLIENT_HINT.setup}
                // The redesign says how much was read on the findings card,
                // beside its date; the head only speaks when the read failed.
                status={MERCHANT_V2 ? unreadText ?? undefined : statusLine(audit, crawlDay, unreadText)}
                actions={scanControl}
            />
            {MERCHANT_V2 && <VisibilityTabs value="setup" />}
            {MERCHANT_V2 ? (
                // Only what helps: site health, can AI read the site, and is it
                // fast. The issue counts and the findings list stay on the
                // staff Site screen; a count of errors and warnings alarms more
                // than it tells a merchant, and the fixes are ours to make.
                <div className="space-y-5">
                    {/* The tiles stack beside the checks from 1280px, so the
                        checks read as a short list rather than a line across
                        the whole page, with their verdicts far off at the
                        right edge. Below that, the tiles stay one row above. */}
                    <div className="grid grid-cols-1 items-stretch gap-5 xl:grid-cols-[minmax(300px,1fr)_minmax(0,2fr)] [&>*]:min-w-0">
                    <SiteTiles
                        calm
                        stacked
                        client
                        siteHealth={siteHealth}
                        siteHealthAbsent="Not measured on this check."
                        readabilityScore={readabilityScore}
                        readabilityComponents={readabilityComponents}
                        mobile={audit.pageSpeed.runs.find((r) => r.strategy === "mobile")}
                        desktop={audit.pageSpeed.runs.find((r) => r.strategy === "desktop")}
                        errors={null}
                        warnings={null}
                        pagesCrawled={audit.pagesCrawled}
                        crawlUnread={crawlUnread}
                        onOpen={(anchor) => openCard(anchor)}
                    />
                    {machineRead}
                    </div>
                    <ClientReadersCard businessId={business.id} />
                </div>
            ) : (
            <div className="space-y-5">
                <SiteTiles
                    siteHealth={siteHealth}
                    siteHealthAbsent="Not measured on this check."
                    previousSiteHealth={audit.previousSiteHealth}
                    readabilityScore={readabilityScore}
                    readabilityComponents={readabilityComponents}
                    mobile={audit.pageSpeed.runs.find((r) => r.strategy === "mobile")}
                    desktop={audit.pageSpeed.runs.find((r) => r.strategy === "desktop")}
                    errors={active ? pagesWith(active, "error") : null}
                    warnings={active ? pagesWith(active, "warning") : null}
                    issuesNote={roleState.kind === "error" ? "Could not load. Reload to try again." : "Loading…"}
                    pagesCrawled={audit.pagesCrawled}
                    crawlUnread={crawlUnread}
                    crawledAt={audit.crawledAt}
                    onOpen={(anchor) => openCard(anchor === "core-web-vitals" ? "issues" : anchor)}
                    client
                />

                <Bento layout="c2xl">
                    <ClientFindingsCard issues={active} crawlDay={crawlDay} unreadText={unreadText} status={roleStatus} pagesRead={audit.pagesCrawled} />
                    {machineRead}
                </Bento>

                <ClientReadersCard businessId={business.id} />
            </div>
            )}
        </FoldScope>
    );
}
